import React, { useMemo } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import {
  useActivateMatrix,
  useSaveDraft,
  useUpdateMatrix,
} from "../../../queries/allocationMatrix";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../components/utils";
import { Input, Label, TextArea } from "../../../components/shared/InputField";
import {
  AllocationFactor,
  AllocationMatrixStatus,
  NoMatchBehaviour,
  QuantumMode,
  Rule,
  RuleScope,
  useAllocationMatrixStore,
} from "../../../store/useAllocationMatrixStore";

function fmt(n: number | null): string {
  if (n === null) return "—";
  return n.toLocaleString();
}

const QUANTUM_MODE_LABEL: Record<QuantumMode, string> = {
  [QuantumMode.MIN]: "Min",
  [QuantumMode.MAX]: "Max",
  [QuantumMode.RANGE]: "Range",
};

const SCOPE_LABEL: Record<RuleScope, string> = {
  [RuleScope.PER_EMPLOYEE]: "per Employee",
  [RuleScope.PER_GRANT]: "per Grant",
};

const NO_MATCH_LABEL: Record<NoMatchBehaviour, string> = {
  [NoMatchBehaviour.BLOCK]: "Block",
  [NoMatchBehaviour.FALLBACK_RULE]: "Fallback rule (catch-all)",
  [NoMatchBehaviour.MANUAL_OVERRIDE]: "Manual override",
};

function SectionCard({
  icon,
  title,
  count,
  children,
}: {
  icon: string;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <VStack className="border rounded-lg overflow-hidden">
      <HStack className="items-center justify-between px-4 py-3 border-b bg-white">
        <HStack className="items-center gap-2">
          <Icon icon={icon} className="text-orange-501" height={18} />
          <span className="text-sm font-semibold text-black-501">{title}</span>
        </HStack>
        {count !== undefined && (
          <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
            {count}
          </span>
        )}
      </HStack>
      {children}
    </VStack>
  );
}

function FactorRow({ field }: { field: AllocationFactor }) {
  const values = field.valueSet ?? [];
  const SHOW_MAX = 6;
  const visible = values.slice(0, SHOW_MAX);
  const overflow = values.length - SHOW_MAX;

  return (
    <HStack className="items-start justify-between px-4 py-3 border-b last:border-b-0">
      <VStack className="gap-0.5 w-48 flex-shrink-0">
        <span className="text-sm font-semibold text-black-501">
          {field.name}
        </span>
        <span className="text-xs text-gray-400">{field.fieldPath}</span>
      </VStack>
      <HStack className="flex-1 flex-wrap gap-1.5 justify-end">
        {visible.map((v) => (
          <span
            key={v}
            className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700"
          >
            {v}
          </span>
        ))}
        {overflow > 0 && (
          <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-500">
            +{overflow}
          </span>
        )}
        {values.length === 0 && (
          <span className="text-xs text-gray-400 italic">no values</span>
        )}
      </HStack>
    </HStack>
  );
}

function RuleSummaryRow({
  rule,
  index,
  fields,
}: {
  rule: Rule;
  index: number;
  fields: AllocationFactor[];
}) {
  const conditions: Record<string, string[]> =
    typeof rule.conditions === "object" && rule.conditions !== null
      ? rule.conditions
      : {};
  const fieldMap = Object.fromEntries(fields.map((f) => [f.fieldPath, f]));

  type ConditionChip = { key: string; label: string; isAny: boolean };
  const conditionChips: ConditionChip[] = fields.map((field) => {
    const vals =
      (conditions[field.fieldPath] ?? []).length > 0
        ? conditions[field.fieldPath]
        : field.valueSet ?? [];
    return {
      key: field.fieldPath,
      label: `${field.name} ∈ ${vals.join(", ")}`,
      isAny: false,
    };
  });

  const quantumParts: string[] = [];
  if (rule.quantumMode === QuantumMode.MIN) {
    quantumParts.push(fmt(rule.quantumMin), fmt(rule.quantumRecommended));
  } else if (rule.quantumMode === QuantumMode.MAX) {
    quantumParts.push(fmt(rule.quantumMax), fmt(rule.quantumRecommended));
  } else {
    quantumParts.push(
      fmt(rule.quantumMin),
      fmt(rule.quantumRecommended),
      fmt(rule.quantumMax)
    );
  }

  return (
    <HStack className="px-4 py-3 border-b last:border-b-0 items-start gap-4">
      {/* # */}
      <span className="w-6 flex-shrink-0 text-sm text-gray-500">
        {index + 1}
      </span>

      {/* CONDITIONS */}
      <HStack className="flex-1 flex-wrap gap-1.5">
        {conditionChips.length === 0 ? (
          <span className="text-xs text-gray-400 italic">Any</span>
        ) : (
          conditionChips.map((chip) => (
            <span
              key={chip.key}
              className={`px-2 py-0.5 border rounded text-xs ${
                chip.isAny
                  ? "border-gray-200 bg-white text-gray-400 italic"
                  : "border-gray-200 bg-gray-50 text-gray-700"
              }`}
            >
              {chip.label}
            </span>
          ))
        )}
      </HStack>

      {/* MODE */}
      <span className="w-16 flex-shrink-0 text-sm text-gray-700">
        {QUANTUM_MODE_LABEL[rule.quantumMode]}
      </span>

      {/* SCOPE */}
      <span className="w-28 flex-shrink-0 text-sm text-gray-700">
        {SCOPE_LABEL[rule.scope]}
      </span>

      {/* QUANTUM */}
      <span className="w-36 flex-shrink-0 text-sm text-gray-700 text-right">
        {quantumParts.join(" - ")}
      </span>
    </HStack>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface AllocationMatrixReviewProps {
  onStepChange: (e?: number) => void;
  onBackClick: () => void;
  readOnly?: boolean;
}

const AllocationMatrixReview = (props: AllocationMatrixReviewProps) => {
  const { onBackClick, readOnly = false } = props;
  const navigate = useNavigate();
  const store = useAllocationMatrixStore();

  const { mutate: saveDraftMutate, isLoading: isSaving } = useSaveDraft();
  const { mutate: activateMatrixMutate, isLoading: isActivating } =
    useActivateMatrix();
  const { mutate: updateMatrix, isLoading: isUpdating } = useUpdateMatrix();
  const isSubmitting = isSaving || isActivating || isUpdating;

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!store.name.trim()) errors.push("Matrix name is required.");
    return errors;
  }, [store.name]);

  const canActivate = validationErrors.length === 0;

  function buildPayload() {
    return store.getMatrix();
  }

  function handleActivate() {
    if (!canActivate) return;
    const payload = buildPayload();

    // Editing an ACTIVE matrix → PUT (creates new version, supersedes old)
    if (store.matrixId && store.status === AllocationMatrixStatus.ACTIVE) {
      updateMatrix(
        {
          matrixId: store.matrixId,
          data: {
            ...payload,
            status: AllocationMatrixStatus.ACTIVE,
            id: store.matrixId,
          },
        },
        {
          onSuccess: () => {
            toast(
              "Matrix saved. A new version is now active and the previous one has been superseded.",
              { type: "success", autoClose: 3000 }
            );
            store.clear();
            navigate("/settings/companyProfile", {
              state: { tab: "Allocation Matrix" },
            });
          },
          onError: (err: any) => {
            toast(err?.response?.data?.reason || "Failed to save matrix.", {
              type: "error",
              autoClose: 3000,
            });
          },
        }
      );
    } else {
      // New matrix or existing DRAFT → POST / (transitions DRAFT→ACTIVE or fresh ACTIVE)
      activateMatrixMutate(
        {
          ...payload,
          status: AllocationMatrixStatus.ACTIVE,
          id: store.matrixId,
        },
        {
          onSuccess: () => {
            toast("Matrix activated successfully!", {
              type: "success",
              autoClose: 2000,
            });
            store.clear();
            navigate("/settings/companyProfile", {
              state: { tab: "Allocation Matrix" },
            });
          },
          onError: (err: any) => {
            toast(err?.response?.data?.reason || "Failed to activate matrix.", {
              type: "error",
              autoClose: 3000,
            });
          },
        }
      );
    }
  }

  function handleSaveDraft() {
    const payload = buildPayload();
    // POST /save-draft — creates new DRAFT or updates existing DRAFT (id optional)
    saveDraftMutate(
      { ...payload, status: AllocationMatrixStatus.DRAFT, id: store.matrixId },
      {
        onSuccess: (res: any) => {
          const id = res?.data?.data?.id;
          if (id) store.setMatrixId(id);
          toast(
            "Draft saved successfully. You can continue editing or activate later.",
            { type: "success", autoClose: 3000 }
          );
        },
        onError: (err: any) => {
          toast(
            err?.response?.data?.reason ||
              "Failed to save draft. Please try again.",
            { type: "error", autoClose: 3000 }
          );
        },
      }
    );
  }

  return (
    <VStack className="w-full h-full">
      {/* Header */}
      <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
        <HStack className="flex justify-between font-semibold text-lg1 text-black-501">
          Review &amp; Activate
        </HStack>
        <HStack className="flex font-medium text-sm3 text-gray-401">
          Review the matrix, set scope, and activate to start evaluating new
          grants.
        </HStack>
      </VStack>

      {/* Body */}
      <VStack className="flex-1 overflow-y-auto px-6 pt-6 pb-2 gap-6">
        {/* ── Matrix metadata ── */}
        <SectionCard icon="ri:file-list-3-line" title="Matrix metadata">
          <VStack className="gap-4 px-4 py-4">
            <div>
              <Label className="text-sm font-normal">
                Matrix name <span className="text-rose-400">*</span>
              </Label>
              <Input
                type="text"
                placeholder="e.g. India ESOP Allocation FY26"
                value={store.name}
                disabled={readOnly}
                onChange={(e) => store.setName(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-normal">Description</Label>
              <TextArea
                placeholder="Describe what this matrix is for..."
                value={store.description}
                disabled={readOnly}
                onChange={(e) => store.setDescription(e.target.value)}
              />
            </div>
          </VStack>
        </SectionCard>

        {/* ── Factors ── */}
        <SectionCard
          icon="ri:stack-line"
          title="Factors"
          count={store.selectedFactors.length}
        >
          {store.selectedFactors.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-400 italic">
              No factors selected.
            </p>
          ) : (
            store.selectedFactors.map((field) => (
              <FactorRow key={field.fieldPath} field={field} />
            ))
          )}
        </SectionCard>

        {/* ── Rules ── */}
        <SectionCard
          icon="ri:equalizer-line"
          title="Rules (priority order)"
          count={store.rules.length}
        >
          {store.rules.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-400 italic">
              No rules added.
            </p>
          ) : (
            <>
              {/* Table header */}
              <HStack className="px-4 py-2 bg-gray-50 border-b gap-4">
                <span className="w-6 text-xs font-semibold text-gray-500 uppercase">
                  #
                </span>
                <span className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Conditions
                </span>
                <span className="w-16 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Mode
                </span>
                <span className="w-28 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Scope
                </span>
                <span className="w-36 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">
                  Quantum
                </span>
              </HStack>
              {store.rules.map((rule, i) => (
                <RuleSummaryRow
                  key={i}
                  rule={rule}
                  index={i}
                  fields={store.selectedFactors}
                />
              ))}
            </>
          )}
        </SectionCard>

        {/* ── No-match behaviour ── */}
        <VStack className="border rounded-lg px-4 py-3 gap-0.5">
          <p className="text-sm font-semibold text-black-501">
            No-match behaviour
          </p>
          <p className="text-sm text-gray-500">
            {NO_MATCH_LABEL[store.noMatchBehaviour]}
          </p>
        </VStack>

        {/* ── Validation banner ── */}
        {!canActivate && (
          <VStack className="border border-orange-300 bg-orange-50 rounded-lg px-4 py-3 gap-1.5">
            <HStack className="items-center gap-2">
              <Icon
                icon="ri:alert-line"
                className="text-orange-501 flex-shrink-0"
                height={16}
              />
              <span className="text-sm font-semibold text-orange-600">
                Cannot activate yet
              </span>
            </HStack>
            <ul className="list-disc list-inside pl-1 space-y-0.5">
              {validationErrors.map((err) => (
                <li key={err} className="text-sm text-orange-600">
                  {err}
                </li>
              ))}
            </ul>
          </VStack>
        )}
      </VStack>

      {/* Footer */}
      <HStack className="justify-between px-6 pt-4 pb-4 border-t-[0.5px]">
        <ButtonPrimary1 onClick={onBackClick}>
          <Icon
            icon="ri:arrow-left-s-line"
            height={16}
            className="inline mr-1"
          />
          Back
        </ButtonPrimary1>
        <HStack className="gap-3">
          <ButtonPrimary1
            onClick={handleSaveDraft}
            disabled={isSubmitting || readOnly}
          >
            <Icon icon="ri:save-line" height={16} className="inline mr-1" />
            Save draft
          </ButtonPrimary1>
          <ButtonPrimary
            onClick={handleActivate}
            disabled={!canActivate || isSubmitting || readOnly}
            loading={isSubmitting}
            className={
              !canActivate || readOnly ? "opacity-50 cursor-not-allowed" : ""
            }
          >
            <Icon icon="ri:check-line" height={16} className="inline mr-1" />
            Activate matrix
          </ButtonPrimary>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default AllocationMatrixReview;
